import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { FaRoute, FaSearch } from "react-icons/fa";
import PageLayout from "../../../components/layout/PageLayout";
import { Header } from "../../../components/ui";
import api from "../../../services/api";
import styles from "./RouteDetailPage.module.css";

const ROUTE_STATUSES = ["Сформирован", "Открыт", "Закрыт"];
const RECEIPT_STATUSES = ["Открыта", "Закрыта", "Не доставлено"];

export default function RouteDetailPage() {
  const { id } = useParams();
  const [route, setRoute] = useState(null);
  const [search, setSearch] = useState("");
  const [markInputs, setMarkInputs] = useState({});

  useEffect(() => {
    api.get(`/routes/${id}`).then(setRoute);
  }, [id]);

  const receipts = useMemo(() => {
    if (!search.trim()) return route?.receipts || [];
    return (route?.receipts || []).filter(r =>
      String(r.receipt_number).includes(search.trim())
    );
  }, [route, search]);

  if (!route) {
    return (
      <PageLayout
        header={<Header icon={FaRoute} title="Маршрут" subtitle="Загрузка…" />}
      >
        <p className={styles.loading}>Загрузка маршрута…</p>
      </PageLayout>
    );
  }

  const updateProduct = (receiptId, productId, updater) => {
    setRoute(prev => ({
      ...prev,
      receipts: prev.receipts.map(r =>
        r.id !== receiptId
          ? r
          : {
              ...r,
              products: r.products.map(p =>
                p.id === productId ? updater(p) : p
              ),
            }
      ),
    }));
  };

  const addMark = async (receiptId, product) => {
    const raw = markInputs[product.id];
    const value = raw?.trim();

    if (!value) {
      console.warn("Пустая марка, запрос не отправлен");
      return;
    }

    try {
      await api.post(`/products/${product.id}/marks`, {
        marks: [value],
      });

      updateProduct(receiptId, product.id, p => ({
        ...p,
        marks: [...(p.marks || []), value],
      }));

      setMarkInputs(prev => ({ ...prev, [product.id]: "" }));
    } catch (err) {
      console.error(
        "Ошибка добавления марки",
        err.response?.data || err.message
      );
      alert(
        err.response?.data?.error ||
        "Ошибка добавления марки"
      );
    }
  };


  const deleteMark = async (receiptId, product, index) => {
    await api.delete(`/products/${product.id}/marks/${index}`);

    updateProduct(receiptId, product.id, p => ({
      ...p,
      marks: p.marks.filter((_, i) => i !== index),
    }));
  };

  return (
    <PageLayout
      header={
        <Header
          icon={FaRoute}
          title={`Маршрут ${route.route_number}`}
          subtitle={`ID ${route.id} · ${route.route_date}`}
        />
      }
    >
      {/* ROUTE SUMMARY */}
      <div className={styles.routeCard}>
        <div className={styles.routeGrid}>
          <div><span>Водитель</span><strong>{route.driver}</strong></div>
          <div><span>Авто</span><strong>{route.car}</strong></div>
          <div><span>Районы</span><strong>{route.route_districts}</strong></div>
          <div><span>Время</span><strong>{route.time_from} – {route.time_to}</strong></div>
        </div>

        <div className={styles.routeControls}>
          <select
            value={route.route_status}
            onChange={async e => {
              const v = e.target.value;
              await api.patch(`/routes/${id}/status`, { route_status: v });
              setRoute(prev => ({ ...prev, route_status: v }));
            }}
          >
            {ROUTE_STATUSES.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      {/* SEARCH */}
      <div className={styles.searchBox}>
        <FaSearch />
        <input
          placeholder="Поиск по номеру квитанции"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* RECEIPT CARDS GRID */}
      <div className={styles.cardsGrid}>
        {receipts.map(r => (
          <div key={r.id} className={styles.card}>
            {/* HEADER */}
            <div className={styles.cardHeader}>
              <div>
                <h3>Квитанция {r.receipt_number}</h3>
                <p>ID: {r.id}</p>
              </div>

              <select
                value={r.status}
                onChange={async e => {
                  const v = e.target.value;
                  await api.patch(`/receipts/${r.id}/status`, { status: v });
                  setRoute(prev => ({
                    ...prev,
                    receipts: prev.receipts.map(x =>
                      x.id === r.id ? { ...x, status: v } : x
                    ),
                  }));
                }}
              >
                {RECEIPT_STATUSES.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            {/* META */}
            <div className={styles.meta}>
              <div><span>Клиент</span>{r.client_name}</div>
              <div><span>Адрес</span>{r.delivery_address}</div>
              <div><span>Договор</span>{r.contract_number}</div>
              <div><span>Оплата</span>{r.payment_method}</div>
            </div>

            {/* PRODUCTS */}
            <div className={styles.products}>
              {r.products.map(p => (
                <div key={p.id} className={styles.product}>
                  <div className={styles.productHeader}>
                    <strong>{p.product_name}</strong>
                    <span>{p.actual_quantity} шт</span>
                  </div>

                  <div className={styles.marks}>
                    {p.marks?.map((m, i) => (
                      <span key={i} className={styles.mark}>
                        {m}
                        <button onClick={() => deleteMark(r.id, p, i)}>×</button>
                      </span>
                    ))}

                    <input
                      placeholder="+ марка"
                      value={markInputs[p.id] || ""}
                      onChange={e =>
                        setMarkInputs(prev => ({
                          ...prev,
                          [p.id]: e.target.value,
                        }))
                      }
                    />
                    <button onClick={() => addMark(r.id, p)}>+</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </PageLayout>
  );
}
